import React from 'react';
import { motion } from 'framer-motion';
import { Calendar, MapPin, Clock, Heart } from 'lucide-react';

const EventCard = ({ event, onCardClick, onToggleFavorite, isFavorite, viewMode }) => {
    const formatEventDate = (dateString) => {
        if (!dateString) return { date: '', time: '' };
        const date = new Date(dateString);
        return {
            date: date.toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" }),
            time: date.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })
        };
    };

    const isUpcoming = (dateString) => new Date(dateString) > new Date();

    const getEventTypeColor = (type) => ({
        'konferans': 'bg-blue-100 text-blue-800',
        'seminer': 'bg-green-100 text-green-800',
        'workshop': 'bg-purple-100 text-purple-800',
        'senlik': 'bg-orange-100 text-orange-800',
        'toren': 'bg-red-100 text-red-800',
        'diger': 'bg-gray-100 text-gray-800'
    }[type] || 'bg-gray-100 text-gray-800');

    const getEventTypeText = (type) => ({
        'konferans': 'Konferans',
        'seminer': 'Seminer',
        'workshop': 'Workshop',
        'senlik': 'Şenlik',
        'toren': 'Tören',
        'diger': 'Diğer'
    }[type] || 'Diğer');

    const eventDate = formatEventDate(event.event_date);
    const upcoming = isUpcoming(event.event_date);

    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } }
    };

    return (
        <motion.div
            variants={itemVariants}
            whileHover={{ y: -5, scale: 1.02 }}
            className={`group bg-white dark:bg-gray-800 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-100 dark:border-gray-700 cursor-pointer ${
                viewMode === 'list' ? 'flex flex-row' : 'flex flex-col'
            }`}
            onClick={() => onCardClick(event)}
        >
            <div className={`relative overflow-hidden ${viewMode === 'list' ? 'w-1/3 h-full' : 'w-full h-48'}`}>
                <motion.img
                    src={event.image_url || '/placeholder-image.jpg'}
                    alt={event.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    onError={(e) => {
                        e.target.src = '/placeholder-image.jpg';
                    }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                
                <div className="absolute top-3 left-3">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getEventTypeColor(event.event_type)}`}>
                        {getEventTypeText(event.event_type)}
                    </span>
                </div>
                
                {upcoming && (
                    <div className="absolute top-3 right-3">
                        <span className="bg-green-500 text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1">
                            <Clock size={12} /> Yaklaşan
                        </span>
                    </div>
                )}

                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        onToggleFavorite(event.id);
                    }}
                    className="absolute bottom-3 right-3 bg-white/20 backdrop-blur-sm p-2 rounded-full hover:bg-white/40 transition-all"
                >
                    <Heart size={16} className={isFavorite ? 'text-red-500 fill-current' : 'text-white'} />
                </button>
            </div>

            <div className="p-5 flex flex-col flex-1">
                <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2 leading-tight group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                    {event.title}
                </h3>
                
                <div className="flex items-center text-gray-500 dark:text-gray-400 text-sm mb-3">
                    <Calendar size={14} className="mr-2" />
                    <span>{eventDate.date} - {eventDate.time}</span>
                </div>

                {event.location && (
                    <div className="flex items-center text-gray-500 dark:text-gray-400 text-sm mb-4">
                        <MapPin size={14} className="mr-2" />
                        <span>{event.location}</span>
                    </div>
                )}
                
                <p className="text-gray-600 dark:text-gray-300 text-sm flex-grow mb-4">
                    {event.description && event.description.length > 100 
                        ? `${event.description.substring(0, 100)}...` 
                        : event.description
                    }
                </p>

                <div className="mt-auto pt-4 border-t border-gray-100 dark:border-gray-700 text-right">
                    <span className="text-blue-500 font-semibold text-sm group-hover:underline">
                        Detayları Gör
                    </span>
                </div>
            </div>
        </motion.div>
    );
};

export default EventCard; 